import { Breakpoints, css, styled } from '@volkswagen-onehub/components-core';

export const HEADER_HEIGHT = 48;

// PDF print mode constants
export const PDF_PAGE_WIDTH = 816; // US Letter width (8.5 inches) at 96 DPI
export const PDF_PAGE_MARGIN = 72; // 1 inch margins (72px = 1 inch at 96 DPI)

export const GuideLayout = styled.div<{ $isPrintMode?: boolean }>`
  display: flex;
  min-height: 100vh;

  ${({ $isPrintMode }) =>
    $isPrintMode &&
    css`
      display: block !important;
      min-height: auto !important;
      background-color: white;
    `}
`;

export const NavigationLayout = styled.section<{
  $isOpen?: boolean;
  $headerToggle?: boolean;
  $isMobile?: boolean;
}>`
  display: flex;
  flex-direction: column;

  position: fixed;
  inset: 0;
  z-index: 1000;

  background: ${(props) => props.theme.colors.background.primary};
  transform: translateX(${(props) => (props.$isOpen ? '0' : '-100%')});

  padding:  ${(props) => props.$isMobile ? `${HEADER_HEIGHT}px 0 10px 0` : `${HEADER_HEIGHT}px 0 ${36}px 0`};
  
  /* inset: 0 should fill the viewport, but Chrome mobile sometimes needs help */
  /* Use -webkit-fill-available for better mobile browser support */
  height: -webkit-fill-available;
  height: 100dvh;

  @media (min-width: ${Breakpoints.b1280}px) {
    position: sticky;
    inset: auto;
    top: ${HEADER_HEIGHT}px;
    flex: 0 0 534px;
    transform: translateX(0);
    box-shadow: none;
    align-self: flex-start;
    height: calc(100vh - ${HEADER_HEIGHT}px);

    padding: ${(HEADER_HEIGHT * 3) / 4}px 0 ${(HEADER_HEIGHT * 3) / 4}px 0; 
    padding-right: calc(100% * 0.4 / 24);

    z-index: 1;
  }
`;

export const NavigationContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0; /* Important for flex scrolling */
  width: calc(100% * 20 / 24);
  margin: 0 auto;

  @media (min-width: ${Breakpoints.b560}px) {
    width: 100%;
    padding: 0 64px;
  }

  @media (min-width: ${Breakpoints.b1280}px) {
    padding: 0 29px;
  }
`;

export const NavigationScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0; /* Important for flex scrolling */
  overflow-y: auto;
  overflow-x: hidden;
`;

export const NavigationFooter = styled.div`
  flex-shrink: 0; /* Prevent footer from shrinking */
`;

export const SidebarOverlay = styled.div<{ $isOpen?: boolean }>`
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;

  @media (min-width: ${Breakpoints.b1280 + 1}px) {
    display: none;
  }
`;

export const MenuToggleButton = styled.button`
  display: flex;
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 1001;
  background: ${(props) => props.theme.colors.background.primary};
  border: 1px solid ${(props) => props.theme.colors.border.primary};
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (min-width: ${Breakpoints.b1280 + 1}px) {
    display: none;
  }
`;

export const ContentLayout = styled.section<{
  $headerToggle?: boolean;
  $isPrintMode?: boolean;
}>`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  ${({ $isPrintMode }) =>
    $isPrintMode
      ? css`
          display: block !important;
          min-height: auto !important;
          padding: 0 !important;
          background-color: white;
        `
      : css`
          padding: 22px 0 0px 0;
          background-color: ${(props) => props.theme.colors.background.primary};

          @media (min-width: ${Breakpoints.b1280}px) {
            padding: 84px 0 0 0px;
          }
        `}
`;

export const ContentContainer = styled.div<{ $isPrintMode?: boolean }>`
  ${({ $isPrintMode }) =>
    $isPrintMode
      ? css`
          width: 100% !important;
          padding: ${PDF_PAGE_MARGIN}px !important;
          background: white;
          box-sizing: border-box;
        `
      : css`
          width: 100%;
          margin: 0 auto;
          padding: 24px 16px;
          flex: 1 1 auto;

          @media (min-width: ${Breakpoints.b560}px) {
            padding: 32px 0;
          }

          @media (min-width: ${Breakpoints.b1280}px) {
            width: calc(100% * 15.8 / 16);
            padding-top: 0;
            padding-bottom: 64px;
          }

          @media (min-width: ${Breakpoints.b2560}px) {
            max-width: 1500px;
            margin: 0 auto;
          }
        `}
`;

export const FullWidthContainer = styled.div<{ $isPrintMode?: boolean }>`
  ${({ $isPrintMode }) =>
    $isPrintMode
      ? css`
          width: 100%;
          margin: 0;

          @media print {
            width: 100% !important;
            margin: 0 !important;
          }
        `
      : css`
          width: 100%;
        `}
`;

export const StickyContentContainer = styled.div`
  width: calc(100% * 20 / 24);
  margin: 0 auto;
  flex: 1 1 auto;

  @media (min-width: ${Breakpoints.b1280}px) {
    width: calc(100% * 13 / 16);
    margin-left: calc(100% * 1 / 16);
  }

  @media (min-width: ${Breakpoints.b2560}px) {
    max-width: 1500px;
    margin: 0 auto;
  }
`;

export const TabLink = styled.button`
  background: none;
  border: none;
  outline: none;
  text-align: left;

  cursor: pointer;
  text-decoration: underline;
  transition: color 0.15s;

  padding: 0px 12px;
  margin: 0 -12px;
  margin-bottom: 2.64px;
`;
